import { Craftsman } from '../../types';

/**
 * 曝光率计算服务
 * 根据多个因素计算工匠的曝光分数，用于搜索排序
 */
export class ExposureService {
  /**
   * 计算工匠的曝光分数
   * @param craftsman 工匠信息
   * @returns 曝光分数（越高越好）
   */
  static calculateExposureScore(craftsman: Craftsman & {
    badgeLevel?: 'none' | 'excellent' | 'good' | 'warning' | 'risk';
    completedJobs?: number;
    cancelledJobs?: number;
    avgResponseTime?: number; // 平均响应时间（分钟）
    recentGoodReviews?: number; // 最近30天5星评价数
  }): number {
    let score = 0;

    // 1. 基础评分（0-50分）
    // rating 是 1-5 星，乘以 10 得到 10-50 分
    score += (craftsman.rating || 0) * 10;

    // 2. 徽章加成（-50 到 +30分）
    const badgeScores: Record<string, number> = {
      excellent: 30,
      good: 10,
      warning: -10,
      risk: -50,
      none: 0,
    };
    score += badgeScores[craftsman.badgeLevel || 'none'] || 0;

    // 3. Premium会员加成（+20分）
    if (craftsman.isPremium) {
      score += 20;
    }

    // 4. 评价数量加成（0-10分）
    // 每10个评价+1分，最多+10分
    const reviewCountBonus = Math.min(Math.floor((craftsman.numReviews || 0) / 10), 10);
    score += reviewCountBonus;

    // 5. 完成率加成（0-10分）
    const completedJobs = craftsman.completedJobs || 0;
    const cancelledJobs = craftsman.cancelledJobs || 0;
    const totalJobs = completedJobs + cancelledJobs;
    
    if (totalJobs > 0) {
      const completionRate = completedJobs / totalJobs;
      if (completionRate >= 0.95) {
        score += 10; // 完成率 ≥95%
      } else if (completionRate >= 0.90) {
        score += 5; // 完成率 ≥90%
      }
    }

    // 6. 响应速度加成（0-5分）
    // 平均响应时间 < 5分钟：+5分
    // 平均响应时间 < 15分钟：+3分
    // 平均响应时间 < 30分钟：+1分
    if (craftsman.avgResponseTime !== undefined) {
      if (craftsman.avgResponseTime < 5) {
        score += 5;
      } else if (craftsman.avgResponseTime < 15) {
        score += 3;
      } else if (craftsman.avgResponseTime < 30) {
        score += 1;
      }
    }

    // 7. 近期好评加成（0-20分）
    // 最近30天每1个5星评价+2分，最多+20分
    if (craftsman.recentGoodReviews) {
      score += Math.min(craftsman.recentGoodReviews * 2, 20);
    }

    return Math.round(score * 10) / 10; // 保留1位小数
  }

  /**
   * 计算曝光倍数
   * @param badgeLevel 徽章等级
   * @returns 曝光倍数（0.1 - 1.5）
   */
  static getExposureMultiplier(
    badgeLevel?: 'none' | 'excellent' | 'good' | 'warning' | 'risk'
  ): number {
    const multipliers: Record<string, number> = {
      excellent: 1.5, // 优秀等级：曝光率提升50%
      good: 1.0,      // 良好等级：正常曝光
      warning: 0.5,   // 警告等级：曝光率降低50%
      risk: 0.1,      // 风险等级：曝光率降低90%
      none: 1.0,      // 无等级：正常曝光
    };

    return multipliers[badgeLevel || 'none'] || 1.0;
  }

  /**
   * 获取搜索排序权重
   * 用于在搜索时对工匠进行排序
   */
  static getSortWeight(craftsman: Craftsman & {
    badgeLevel?: 'none' | 'excellent' | 'good' | 'warning' | 'risk';
    completedJobs?: number;
    cancelledJobs?: number;
    avgResponseTime?: number;
    recentGoodReviews?: number;
  }): number {
    // 使用曝光分数作为排序权重
    return this.calculateExposureScore(craftsman);
  }
}



